// Workspace de Prova (não-agendada) — análogo ao StudyWorkspace mas com:
//   - Cronômetros (geral + por questão, conforme config)
//   - Render sem feedback de acerto/erro
//   - Tema/dificuldade opcionalmente ocultos
//   - Sem animação/vibração de acerto-erro (foco em avaliação)
//   - Persistência via /bank-prova-active (autosave debounced)
//   - Encerramento via /bank-prova-end (calc stats no server)
//
// Estados: loading → ready → encerrando → encerrada (redireciona pra
// resultado). Roteamento via history.pushState — mesmo padrão do estudo.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  AlignLeft,
  ChevronDown,
  ChevronUp,
  Clock,
  Grid3x3,
  List as ListIcon,
  TimerReset,
  ChevronLeft,
  ChevronRight,
  Flag,
  Image as ImageIcon,
  Pause,
  Play,
  Search,
  SlidersHorizontal,
  Table as TableIcon,
  Timer,
  Hourglass,
  X as XIcon
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { AnimatedTabs } from "@app/ui/components/animated-tabs";
import {
  api,
  ApiError,
  type BankProvaResponse,
  type EssayResponseState,
  type FetchProgress,
  type ProvaState,
  type ProvaConfig,
  type QuestaoStudy
} from "./api";
import { useModalGrowShrink } from "./modalOrigin";
import { enterProvaResultView } from "./ProvaResultView";
import { openBank } from "./BankDetailView";
import { activeNav } from "./App";
import {
  DifficultyStars,
  QuestionBadges,
  questaoHasImages,
  questaoHasTabelas
} from "./questionBadges";
import {
  FilterRow,
  FilterSection,
  isAnulada,
  Lightbox,
  lightboxImg,
  QuestionTable,
  renderBlock,
  renderInline
} from "./StudyWorkspace";
import {
  QuestionListBody,
  QuestionListSearchInput,
  QuestionListSidebar,
  applyAgrupamento as applySharedAgrupamento,
  type QListAgrupamento
} from "./QuestionListSidebar";
import { QuestionCard } from "./QuestionCard";
import { openAlert } from "./ConfirmPromptModal";
import {
  uiPrefs,
  toggleProvaHeaderCompact,
  toggleQuestionListCollapsed
} from "./uiPrefs";
import { isPlayerOpen } from "./MusicPlayer";
import { WorkspaceHeader, type WorkspaceHeaderAction } from "./WorkspaceHeader";

// =====================
// State
// =====================

type Phase = "loading" | "error" | "ready" | "encerrando" | "encerrada";

export interface ProvaWorkspaceState {
  bankId: string;
  phase: Phase;
  data: BankProvaResponse | null;
  prova: ProvaState | null;
  currentIdx: number;
  errorMsg: string | null;
  /** Progresso do fetch da prova — pra mostrar barra durante phase=loading
   *  com bancos pesados (Karol 2026-05-24: ~10MB de imagens base64). */
  loadProgress?: FetchProgress | null;
}

export const provaWorkspace = signal<ProvaWorkspaceState | null>(null);
export const provaSyncState = signal<"idle" | "pending" | "saving" | "saved" | "error">(
  "idle"
);
export const provaEncerrarConfirmOpen = signal<boolean>(false);
// Tick periódico só pra forçar re-render dos cronômetros.
const _tick = signal<number>(0);

// =====================
// Modos de exibição dos cronômetros — clique cicla normal → âmbar → oculto.
// Persistido por usuário (não por prova) em localStorage; espelha o padrão
// do legado (Simulador.html cycleProvaTimerModeOne).
// =====================

export type TimerMode = "normal" | "amber" | "hidden";
const TIMER_MODES: TimerMode[] = ["normal", "amber", "hidden"];
export type TimerKey = "geral" | "maximo" | "atual" | "media";

const TIMER_MODES_KEY = "newstudent:prova:timer_modes";

function readTimerModes(): Record<TimerKey, TimerMode> {
  const defaults: Record<TimerKey, TimerMode> = {
    geral: "normal",
    maximo: "normal",
    atual: "normal",
    media: "normal"
  };
  if (typeof localStorage === "undefined") return defaults;
  try {
    const raw = localStorage.getItem(TIMER_MODES_KEY);
    if (!raw) return defaults;
    const obj = JSON.parse(raw) as Partial<Record<TimerKey, TimerMode>>;
    const out = { ...defaults };
    for (const k of Object.keys(defaults) as TimerKey[]) {
      const v = obj[k];
      if (v && TIMER_MODES.includes(v)) out[k] = v;
    }
    return out;
  } catch {
    return defaults;
  }
}

function writeTimerModes(modes: Record<TimerKey, TimerMode>): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(TIMER_MODES_KEY, JSON.stringify(modes));
  } catch { /* quota / private mode */ }
}

export const provaTimerModes = signal<Record<TimerKey, TimerMode>>(readTimerModes());

export function cycleTimerMode(key: TimerKey) {
  const cur = provaTimerModes.value[key] || "normal";
  const next = TIMER_MODES[(TIMER_MODES.indexOf(cur) + 1) % TIMER_MODES.length];
  const updated = { ...provaTimerModes.value, [key]: next };
  provaTimerModes.value = updated;
  writeTimerModes(updated);
}

const PROVA_PATH_RE = /^\/student\/banks\/([^/]+)\/prova\/andamento\/?$/;

// =====================
// Routing
// =====================

export function enterProvaWorkspace(
  bankId: string,
  opts: { pushHistory?: boolean } = {}
) {
  // Reset filtros ao trocar de banco — pedido Karol 2026-05-24: filtros não
  // devem vazar entre provas de bancos diferentes. Sempre que entrar num
  // banco novo, vai pra estado neutro (sem filtros aplicados).
  // Inclui também provaFiltersPanelOpen pra não abrir o painel com filtros
  // antigos visíveis.
  const prevBankId = provaWorkspace.value?.bankId ?? null;
  if (prevBankId !== bankId) {
    clearProvaFilters();
    provaFilterPanelOpen.value = false;
    provaMapOpen.value = false;
    provaListMobileOpen.value = false;
  }
  provaWorkspace.value = {
    bankId,
    phase: "loading",
    data: null,
    prova: null,
    currentIdx: 0,
    errorMsg: null,
    loadProgress: { loaded: 0, total: null }
  };
  if (opts.pushHistory !== false) {
    const url = `/student/banks/${encodeURIComponent(bankId)}/prova/andamento`;
    if (window.location.pathname !== url) {
      window.history.pushState({ provaBankId: bankId }, "", url);
    }
  }
  void load(bankId);
}

export function exitProvaWorkspace(opts: { pushHistory?: boolean } = {}) {
  // Captura o bankId ANTES de limpar o signal — o callback abaixo
  // (registrado pelo App) usa pra mandar o aluno pra tela do banco.
  const exitingBankId = provaWorkspace.value?.bankId ?? null;
  flushPendingProvaSaveBeforeExit();
  provaWorkspace.value = null;
  if (opts.pushHistory !== false) {
    if (PROVA_PATH_RE.test(window.location.pathname)) {
      window.history.pushState({}, "", "/student/");
    }
  }
  try {
    _onExitCallback?.(exitingBankId);
  } catch (err) {
    console.error("[prova] onExit cb falhou:", err);
  }
}

let _onExitCallback: ((bankId: string | null) => void) | null = null;
export function onProvaWorkspaceExit(cb: (bankId: string | null) => void) {
  _onExitCallback = cb;
}

let popstateBound = false;
export function bindProvaPopstate() {
  if (popstateBound || typeof window === "undefined") return;
  popstateBound = true;
  window.addEventListener("popstate", () => {
    const m = PROVA_PATH_RE.exec(window.location.pathname);
    if (m) {
      const id = decodeURIComponent(m[1]);
      if (!provaWorkspace.value || provaWorkspace.value.bankId !== id) {
        enterProvaWorkspace(id, { pushHistory: false });
      }
    } else {
      if (provaWorkspace.value) {
        flushPendingProvaSaveBeforeExit();
        provaWorkspace.value = null;
      }
    }
  });
}

export function restoreProvaFromUrl() {
  if (typeof window === "undefined") return;
  const m = PROVA_PATH_RE.exec(window.location.pathname);
  if (m) {
    const id = decodeURIComponent(m[1]);
    enterProvaWorkspace(id, { pushHistory: false });
  }
}

// =====================
// Load
// =====================

async function load(bankId: string) {
  try {
    const res = await api.bankProvaWithProgress(bankId, (p) => {
      const cur = provaWorkspace.value;
      if (!cur || cur.bankId !== bankId || cur.phase !== "loading") return;
      provaWorkspace.value = { ...cur, loadProgress: p };
    });
    if (provaWorkspace.value?.bankId !== bankId) return;
    // SAFETY: garante que toda questão tem um id string único e não-vazio.
    // Bancos migrados de versões antigas podem ter q.id undefined/"" — nesse
    // caso TODOS resolvem pra a mesma chave em prova.respostas, e marcar 1
    // marca todas. Cai pra fallback "q-{idx}" preservando posição.
    //
    // MESMA SAFETY pra q.items[i].id (Karol 2026-05-24): items sem id
    // dentro de composta fazem 2 textareas escreverem na MESMA key da
    // bucket (porque EssayAnswerBox cai em `itemId ?? qid` → todos usam
    // qid), causando overwrite + perda visual ao navegar entre questões.
    // Fallback determinístico `<qid>-item-{idx}` mantém unicidade.
    // Fallback `${qid}-i${j}` (não `-item-`) bate com o que
    // student-bank-essay::locateTarget e StudyWorkspace.load usam — autosave
    // e grade da dissertativa dependem desse mesmo formato.
    res.questoes = res.questoes.map((q, i) => {
      const qid =
        typeof q.id === "string" && q.id !== "" ? q.id : `q-${i}`;
      const items = Array.isArray((q as any).items)
        ? (q as any).items.map((it: any, j: number) => ({
            ...it,
            id:
              typeof it?.id === "string" && it.id !== ""
                ? it.id
                : `${qid}-i${j}`
          }))
        : (q as any).items;
      return {
        ...q,
        id: qid,
        ...(items ? { items } : {})
      } as typeof q;
    });
    // Se há prova ativa, retoma direto.
    if (res.provaAtiva) {
      const prova = retomarProvaState(res.provaAtiva);
      // Fallback: se o escopo salvo não casa com NENHUMA questão do banco
      // (IDs migrados, banco editado depois, etc.), reconstrói com todas
      // as questões do banco — preserva respostas que ainda batem por id.
      const escopoCheck = filterEscopo(res.questoes, prova.escopoQuestaoIds);
      if (escopoCheck.length === 0 && res.questoes.length > 0) {
        console.warn(
          "[prova] escopoQuestaoIds salvo não casa com nenhuma questão; reconstruindo com todas e persistindo."
        );
        prova.escopoQuestaoIds = res.questoes.map((q) => String(q.id));
        // Persiste o escopo reconstruído pra não refazer rebuild nas próximas
        // aberturas. Fire-and-forget — se falhar, o fallback do load reaplica.
        void api.saveProvaActive(bankId, prova).catch((err) => {
          console.error("[prova] persist escopo reconstruído falhou:", err);
        });
      }
      const idx = indexFromProva(prova, res.questoes);
      provaWorkspace.value = {
        bankId,
        phase: "ready",
        data: res,
        prova,
        currentIdx: idx,
        errorMsg: null
      };
    } else {
      // Sem ativa: workspace fica em ready mas o cliente vai abrir o
      // ProvaStartModal por cima (controlado fora desse arquivo). Por
      // enquanto deixa prova=null pra sinalizar "precisa configurar".
      provaWorkspace.value = {
        bankId,
        phase: "ready",
        data: res,
        prova: null,
        currentIdx: 0,
        errorMsg: null
      };
    }
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    if (provaWorkspace.value?.bankId !== bankId) return;
    provaWorkspace.value = {
      bankId,
      phase: "error",
      data: null,
      prova: null,
      currentIdx: 0,
      errorMsg: err instanceof Error ? err.message : String(err)
    };
  }
}

// =====================
// Helpers de tempo + estado
// =====================

// Defaults pra config — provas migradas do legado podem chegar sem `config`
// (ou com config parcial). Sem isso, qualquer leitura `prova.config.X`
// explode com "Cannot read properties of undefined".
function normalizeProvaConfig(raw: Partial<ProvaConfig> | undefined | null): ProvaConfig {
  const c = (raw ?? {}) as Partial<ProvaConfig>;
  return {
    tempoAlvoMs: typeof c.tempoAlvoMs === "number" ? c.tempoAlvoMs : 0,
    regressivo: !!c.regressivo,
    mostrarRelogioGeral: c.mostrarRelogioGeral !== false,
    mostrarRelogioQuestao: c.mostrarRelogioQuestao !== false,
    mostrarRelogioRitmo: c.mostrarRelogioRitmo !== false,
    ocultarTema: !!c.ocultarTema,
    ocultarDificuldade: !!c.ocultarDificuldade,
    escopo: c.escopo ?? { tipo: "todas" }
  };
}

// Retoma a prova: zera "questaoEntrouEm" pra começar a contar a partir
// de agora (tempo passado offline não conta na questão). E reseta
// ultimoRetomadaEm pra que o getTempoDecorrido a partir do estado salvo
// (tempoTotalMs) + diff atual seja correto.
// Ao retomar uma prova já em andamento, decidimos se pausa automaticamente
// baseado em quanto tempo o aluno ficou fora. Regra (2026-05-23):
//   - Saiu há < 5 min: retoma SEM pausar (provavelmente só um F5 / nav).
//     Antes a gente sempre pausava → Karol clicava em alternativa atrás do
//     modal de pausa e perdia respostas porque o modal absorvia o click.
//   - Saiu há ≥ 5 min OU prova já estava pausada antes: mantém pausada=true
//     pra não inflar o cronômetro com tempo inativo + pedir ack do aluno.
const RESUME_AUTO_RUN_THRESHOLD_MS = 5 * 60_000;
function retomarProvaState(saved: ProvaState): ProvaState {
  const now = Date.now();
  const lastRetomada =
    typeof saved.ultimoRetomadaEm === "number" ? saved.ultimoRetomadaEm : now;
  const gap = now - lastRetomada;
  const wasPaused = !!saved.pausada;
  const shouldPause = wasPaused || gap >= RESUME_AUTO_RUN_THRESHOLD_MS;
  return {
    ...saved,
    config: normalizeProvaConfig(saved.config),
    pausada: shouldPause,
    ultimoRetomadaEm: now,
    questaoEntrouEm: now
  };
}

// Calcula o índice da currentIdx baseado em questaoAtualId.
function indexFromProva(prova: ProvaState, questoes: QuestaoStudy[]): number {
  if (!prova.questaoAtualId) return 0;
  const escopo = filterEscopo(questoes, prova.escopoQuestaoIds);
  const target = String(prova.questaoAtualId);
  const idx = escopo.findIndex((q) => String(q.id) === target);
  return idx >= 0 ? idx : 0;
}

// Lista filtrada do escopo, na ordem do banco original. Normaliza IDs pra
// string nos dois lados — provas legadas podem ter escopoQuestaoIds como
// number e o q.id da migração pode ser string/number misturados.
export function filterEscopo(
  questoes: QuestaoStudy[],
  escopoIds: string[]
): QuestaoStudy[] {
  const set = new Set((escopoIds ?? []).map((x) => String(x)));
  return questoes.filter((q) => set.has(String(q.id)));
}

// =====================
// Filtros da prova em andamento — atuam SOBRE o escopo já definido
// quando a prova foi iniciada. Diferente do Study (que filtra por status
// acerto/erro/pendente), em prova só há "respondida" vs "não respondida".
// =====================

export type ProvaFilterStatus = "respondida" | "pendente";

export interface ProvaFilters {
  temas: string[];
  fontes: string[];
  dificuldade: number[];
  busca: string;
  somenteCaso: boolean;
  somenteImagem: boolean;
  somenteTabela: boolean;
  status: ProvaFilterStatus[];
}

function emptyProvaFilters(): ProvaFilters {
  return {
    temas: [],
    fontes: [],
    dificuldade: [],
    busca: "",
    somenteCaso: false,
    somenteImagem: false,
    somenteTabela: false,
    status: []
  };
}

export const provaFilters = signal<ProvaFilters>(emptyProvaFilters());
export const provaFilterPanelOpen = signal<boolean>(false);
export const provaMapOpen = signal<boolean>(false);
// Modal mobile da lista de questões (sidebar full-screen).
export const provaListMobileOpen = signal<boolean>(false);
// Agrupamento da lista de questões — não persiste; só preferência de UI da
// sessão atual. Default "tema". Sempre tem valor.
export const provaAgrupamento = signal<"tema" | "fonte">("fonte");

export function updateProvaFilters(partial: Partial<ProvaFilters>) {
  provaFilters.value = { ...provaFilters.value, ...partial };
}

export function clearProvaFilters() {
  provaFilters.value = emptyProvaFilters();
}

export function countActiveProvaFilters(f: ProvaFilters): number {
  let n = 0;
  if (f.temas.length) n++;
  if (f.fontes.length) n++;
  if (f.dificuldade.length && f.dificuldade.length < 5) n++;
  if (f.busca.trim()) n++;
  if (f.somenteCaso) n++;
  if (f.somenteImagem) n++;
  if (f.somenteTabela) n++;
  if (f.status.length) n++;
  return n;
}

// Status simplificado da questão na prova: respondida (tem opcao marcada
// OU, em dissertativa, qualquer texto digitado DURANTE ESTA PROVA) ou pendente.
// Anuladas contam como respondidas (server resolve no encerramento).
//
// CRÍTICO (Karol 2026-05-24): a checagem de essay precisa do parâmetro
// `essayKeysTouched` pra distinguir respostas tipadas durante a prova vs
// respostas legadas do Study mode (estudo.essayResponses é a MESMA bucket).
// Sem touched, qualquer texto pré-existente vazava no counter mesmo a Karol
// não tendo respondido nada na prova ainda. Pra composite, considera tocado
// se QUALQUER um dos items.id está em essayKeysTouched (suficiente — o aluno
// começou a responder a questão).
export function provaQuestaoStatus(
  q: QuestaoStudy,
  respostas: ProvaState["respostas"],
  essayResponses?: Record<string, EssayResponseState>,
  essayKeysTouched?: ReadonlyArray<string> | Set<string>
): ProvaFilterStatus {
  if (isAnulada(q)) return "respondida";
  const r = respostas[q.id];
  if (r && r.opcao != null) return "respondida";
  // Essay (simples ou item de composta). Só conta se a key foi TOCADA nesta
  // prova — ignora bucket pré-existente de Study.
  if (essayResponses && essayKeysTouched) {
    const touchedSet =
      essayKeysTouched instanceof Set
        ? essayKeysTouched
        : new Set(essayKeysTouched);
    // Essay simples no nível da questão.
    if (q.tipo === "essay" && touchedSet.has(String(q.id))) {
      const txt = essayResponses[q.id]?.resposta;
      if (typeof txt === "string" && txt.trim().length > 0) return "respondida";
    }
    // Composite: parente sem tipo, items com tipo="essay". Considera
    // respondida se qualquer item.id está no touched set + tem texto. Aceita
    // também o fallback do parent.id quando legado salvou items sob qid.
    if (Array.isArray(q.items) && q.items.length > 0) {
      for (const it of q.items) {
        const itKey = it && typeof it === "object" && it.id ? String(it.id) : null;
        if (itKey && touchedSet.has(itKey)) {
          const txt = essayResponses[itKey]?.resposta;
          if (typeof txt === "string" && txt.trim().length > 0) return "respondida";
        }
      }
      // Fallback legado: items sem id usavam o qid como key. Conta se qid
      // está touched + bucket tem texto sob qid.
      if (touchedSet.has(String(q.id))) {
        const txt = essayResponses[q.id]?.resposta;
        if (typeof txt === "string" && txt.trim().length > 0) return "respondida";
      }
    }
  }
  return "pendente";
}

// Helper genérico — true se a questão deve contar como respondida no
// contador da prova.
export function isProvaRespondida(
  q: QuestaoStudy,
  respostas: ProvaState["respostas"],
  essayResponses?: Record<string, EssayResponseState>,
  essayKeysTouched?: ReadonlyArray<string> | Set<string>
): boolean {
  return (
    provaQuestaoStatus(q, respostas, essayResponses, essayKeysTouched) ===
    "respondida"
  );
}

function matchesProvaFiltro(
  q: QuestaoStudy,
  f: ProvaFilters,
  respostas: ProvaState["respostas"],
  essayResponses?: Record<string, EssayResponseState>,
  essayKeysTouched?: ReadonlyArray<string> | Set<string>
): boolean {
  if (f.temas.length > 0 && (!q.tema || !f.temas.includes(q.tema))) return false;
  if (f.fontes.length > 0 && (!q.fonte || !f.fontes.includes(q.fonte))) return false;
  if (f.dificuldade.length > 0 && f.dificuldade.length < 5) {
    const d = Number(q.dificuldade ?? 0);
    if (!f.dificuldade.includes(d)) return false;
  }
  if (f.somenteImagem && !(typeof q.imagem_url === "string" && q.imagem_url) && !(Array.isArray(q.imagens) && q.imagens.length > 0)) return false;
  if (f.somenteCaso && !q.caso) return false;
  if (f.somenteTabela && !(Array.isArray(q.tabelas) && q.tabelas.length > 0)) return false;
  if (f.busca) {
    const term = f.busca.toLowerCase();
    const hay = [q.pergunta, q.caso ?? "", ...(q.opcoes ?? [])].join(" ").toLowerCase();
    if (!hay.includes(term)) return false;
  }
  if (f.status.length > 0) {
    const st = provaQuestaoStatus(q, respostas, essayResponses, essayKeysTouched);
    if (!f.status.includes(st)) return false;
  }
  return true;
}

// Lista visível (escopo + filtros). Quando todos os filtros estão vazios,
// retorna o escopo completo. Usada pra navegação e pro mapa.
export function getProvaVisiveis(
  questoes: QuestaoStudy[],
  escopoIds: string[],
  filters: ProvaFilters,
  respostas: ProvaState["respostas"],
  essayResponses?: Record<string, EssayResponseState>,
  essayKeysTouched?: ReadonlyArray<string> | Set<string>
): QuestaoStudy[] {
  const escopo = filterEscopo(questoes, escopoIds);
  if (countActiveProvaFilters(filters) === 0) return escopo;
  return escopo.filter((q) =>
    matchesProvaFiltro(q, filters, respostas, essayResponses, essayKeysTouched)
  );
}

// Tempo total decorrido considerando o estado atual.
export function getTempoDecorridoMs(prova: ProvaState | null): number {
  if (!prova) return 0;
  if (prova.pausada) return prova.tempoTotalMs;
  const diff = Date.now() - prova.ultimoRetomadaEm;
  return prova.tempoTotalMs + Math.max(0, diff);
}

// Tempo da questão atual (do que está acumulado em tempoPorQuestao + diff
// desde questaoEntrouEm).
export function getTempoQuestaoAtualMs(prova: ProvaState | null): number {
  if (!prova || !prova.questaoAtualId) return 0;
  const base = prova.tempoPorQuestao[prova.questaoAtualId] ?? 0;
  if (prova.pausada || !prova.questaoEntrouEm) return base;
  return base + Math.max(0, Date.now() - prova.questaoEntrouEm);
}

// Tempo restante (regressivo) — se config.tempoAlvoMs > 0.
export function getTempoRestanteMs(prova: ProvaState | null): number | null {
  if (!prova || !prova.config || !prova.config.regressivo || !prova.config.tempoAlvoMs) return null;
  return Math.max(0, prova.config.tempoAlvoMs - getTempoDecorridoMs(prova));
}

// Format ms → HH:MM:SS (ou MM:SS se < 1h).
export function formatHMS(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// =====================
// Criar nova prova
// =====================

export function criarProvaState(
  questoes: QuestaoStudy[],
  config: ProvaConfig
): ProvaState | null {
  const escopoIds = resolverEscopoQuestaoIds(questoes, config.escopo);
  if (escopoIds.length === 0) return null;
  const now = Date.now();
  return {
    id: "prova_" + now,
    iniciadaEm: now,
    encerradaEm: null,
    config,
    escopoQuestaoIds: escopoIds,
    respostas: {},
    tempoTotalMs: 0,
    tempoExtraMs: 0,
    tempoPorQuestao: {},
    questaoAtualId: escopoIds[0] ?? null,
    questaoEntrouEm: now,
    ultimoRetomadaEm: now,
    pausada: false,
    essayKeysTouched: [],
    // Bucket prova-scoped (não vaza Study). Karol 2026-05-24.
    essayResponses: {}
  };
}

function resolverEscopoQuestaoIds(
  questoes: QuestaoStudy[],
  escopo: ProvaConfig["escopo"]
): string[] {
  if (escopo.tipo === "todas") {
    return questoes.map((q) => q.id);
  }
  if (escopo.tipo === "personalizar") {
    const setT = new Set(escopo.temas);
    const setF = new Set(escopo.fontes);
    const setD = new Set(escopo.dificuldade);
    return questoes
      .filter((q) => {
        if (setT.size > 0 && (!q.tema || !setT.has(q.tema))) return false;
        if (setF.size > 0 && (!q.fonte || !setF.has(q.fonte))) return false;
        if (setD.size > 0 && setD.size < 5) {
          const d = Number(q.dificuldade ?? 0);
          if (!setD.has(d)) return false;
        }
        return true;
      })
      .map((q) => q.id);
  }
  if (escopo.tipo === "numeros") {
    const nums = parseNumerosRaw(escopo.numerosRaw, questoes.length);
    return questoes
      .filter((_, idx) => nums.has(idx + 1))
      .map((q) => q.id);
  }
  return [];
}

// "1,3,5-8,12" → Set<number> (1-indexed).
export function parseNumerosRaw(raw: string, max: number): Set<number> {
  const out = new Set<number>();
  if (!raw || !raw.trim()) return out;
  const parts = raw.split(/[,;]/);
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const range = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
    if (range) {
      const a = Math.max(1, parseInt(range[1], 10));
      const b = Math.min(max, parseInt(range[2], 10));
      for (let i = a; i <= b; i++) out.add(i);
    } else {
      const n = parseInt(trimmed, 10);
      if (Number.isFinite(n) && n >= 1 && n <= max) out.add(n);
    }
  }
  return out;
}

// Inicia uma prova nova já com o ws aberto. Chamado pelo ProvaStartModal
// depois de o aluno confirmar.
export function iniciarProvaConfigurada(config: ProvaConfig) {
  const ws = provaWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  const prova = criarProvaState(ws.data.questoes, config);
  if (!prova) return;
  provaWorkspace.value = {
    ...ws,
    prova,
    currentIdx: 0
  };
  scheduleProvaSave();
}

// =====================
// Navegação
// =====================

function gotoIdx(idx: number) {
  const ws = provaWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.prova || !ws.data) return;
  const escopo = filterEscopo(ws.data.questoes, ws.prova.escopoQuestaoIds);
  const total = escopo.length;
  if (total === 0) return;
  const clamped = Math.max(0, Math.min(total - 1, idx));
  if (clamped === ws.currentIdx) return;

  // Salva o tempo gasto na questão que está saindo.
  const prova = flushTempoQuestaoAtual(ws.prova);
  const nextQ = escopo[clamped];
  const updated: ProvaState = {
    ...prova,
    questaoAtualId: nextQ?.id ?? prova.questaoAtualId,
    questaoEntrouEm: Date.now()
  };
  provaWorkspace.value = { ...ws, prova: updated, currentIdx: clamped };
  scheduleProvaSave();
}

function gotoPrev() {
  const ws = provaWorkspace.value;
  if (!ws) return;
  gotoIdx(ws.currentIdx - 1);
}

function gotoNext() {
  const ws = provaWorkspace.value;
  if (!ws) return;
  gotoIdx(ws.currentIdx + 1);
}

// Aplica o tempo decorrido na questão atual em tempoPorQuestao e reseta o
// questaoEntrouEm pra agora. Também atualiza tempoTotalMs com o diff geral.
function flushTempoQuestaoAtual(prova: ProvaState): ProvaState {
  if (prova.pausada) return prova;
  const now = Date.now();
  // Geral
  const novoTotal = prova.tempoTotalMs + Math.max(0, now - prova.ultimoRetomadaEm);
  // Por questão
  const tpq = { ...prova.tempoPorQuestao };
  if (prova.questaoAtualId && prova.questaoEntrouEm) {
    const diff = Math.max(0, now - prova.questaoEntrouEm);
    tpq[prova.questaoAtualId] = (tpq[prova.questaoAtualId] ?? 0) + diff;
  }
  return {
    ...prova,
    tempoTotalMs: novoTotal,
    tempoPorQuestao: tpq,
    ultimoRetomadaEm: now,
    questaoEntrouEm: now
  };
}

// =====================
// Responder
// =====================

function setRespostaProva(selectedIdx: number | null) {
  const ws = provaWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.prova || !ws.data) return;
  const escopo = filterEscopo(ws.data.questoes, ws.prova.escopoQuestaoIds);
  const q = escopo[ws.currentIdx];
  if (!q) return;
  // Anulada: aluno pode marcar qualquer alternativa (conta como acerto via
  // regra unificada). Não bloqueamos como no estudo.
  const next = { ...ws.prova.respostas };
  if (selectedIdx === null) {
    delete next[q.id];
  } else {
    // Tempo gasto até agora na questão.
    const tempoMs = getTempoQuestaoAtualMs(ws.prova);
    next[q.id] = {
      opcao: selectedIdx,
      tempoMs: Math.floor(tempoMs),
      respondidaEm: Date.now()
    };
  }
  provaWorkspace.value = {
    ...ws,
    prova: { ...ws.prova, respostas: next }
  };
  scheduleProvaSave();
}

// =====================
// Save debounced + flush
// =====================

const SAVE_DEBOUNCE_MS = 1500;
// Retry com backoff exponencial em erros transientes (rede caiu, 5xx).
// Decisão 2026-05-23: fire-and-forget perdia respostas da Karol quando
// internet oscilava. Agora insiste até dar certo OU bater MAX_SAVE_RETRIES.
const SAVE_RETRY_DELAYS_MS = [500, 1500, 3000, 6000, 12000];
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<void> | null = null;
let savedLingerTimer: ReturnType<typeof setTimeout> | null = null;
let retryAttempt = 0;
let backgroundRetryTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleProvaSave() {
  provaSyncState.value = "pending";
  if (saveTimer) clearTimeout(saveTimer);
  // Se tem retry em background agendado, cancela — o save novo prevalece.
  if (backgroundRetryTimer) {
    clearTimeout(backgroundRetryTimer);
    backgroundRetryTimer = null;
  }
  saveTimer = setTimeout(() => {
    saveTimer = null;
    void flushProvaSave();
  }, SAVE_DEBOUNCE_MS);
}

function isTransientSaveError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const status = (err as { status?: unknown }).status;
  if (typeof status === "number") {
    if (status === 408 || status === 429) return true;
    if (status >= 500 && status <= 599) return true;
    return false;
  }
  const message = String((err as { message?: unknown }).message ?? "").toLowerCase();
  if (
    message.includes("network") ||
    message.includes("failed to fetch") ||
    message.includes("timeout") ||
    message.includes("offline")
  )
    return true;
  return false;
}

async function flushProvaSave(): Promise<void> {
  const ws = provaWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.prova) return;
  const bankId = ws.bankId;

  if (inFlight) {
    try {
      await inFlight;
    } catch { /* ignora */ }
  }
  provaSyncState.value = "saving";
  const doSave = async (): Promise<void> => {
    // Cada attempt pega o snapshot ATUAL — não o que estava em memória quando
    // scheduleProvaSave rodou. Sem isso, retries enviariam estado obsoleto.
    const cur = provaWorkspace.value;
    if (!cur || cur.bankId !== bankId || cur.phase !== "ready" || !cur.prova) return;
    const prova = withCurrentTimes(cur.prova);
    try {
      await api.saveProvaActive(bankId, prova);
      retryAttempt = 0;
      if (provaWorkspace.value?.bankId === bankId) {
        provaSyncState.value = "saved";
        if (savedLingerTimer) clearTimeout(savedLingerTimer);
        savedLingerTimer = setTimeout(() => {
          if (provaSyncState.value === "saved") provaSyncState.value = "idle";
        }, 2200);
      }
    } catch (err) {
      const code =
        err && typeof err === "object" && "code" in err
          ? (err as { code: string }).code
          : null;
      // exam_blocked é DEFINITIVO (admin rebaixou acesso). Não retry.
      if (code === "exam_blocked") {
        const ws = provaWorkspace.value;
        if (ws?.bankId === bankId) {
          provaWorkspace.value = { ...ws, prova: null, currentIdx: 0 };
          provaSyncState.value = "idle";
        }
        retryAttempt = 0;
        void openAlert({
          title: "Erro",
          message:
            (err as Error).message ||
            "Seu acesso a este banco é só para estudo — prova simulada não permitida."
        });
        return;
      }
      // Transient → retry com backoff.
      if (isTransientSaveError(err) && retryAttempt < SAVE_RETRY_DELAYS_MS.length) {
        const delay = SAVE_RETRY_DELAYS_MS[retryAttempt];
        retryAttempt++;
        console.warn(
          `[prova] saveProvaActive falhou (retry ${retryAttempt}/${SAVE_RETRY_DELAYS_MS.length} em ${delay}ms):`,
          err instanceof Error ? err.message : err
        );
        provaSyncState.value = "pending";
        await new Promise((r) => setTimeout(r, delay));
        return doSave();
      }
      // Esgotou retries OU não-transient → marca erro + agenda nova tentativa
      // em 30s pra não desistir definitivamente. Aluno continua respondendo;
      // próximo scheduleProvaSave cancela esse background timer.
      console.error("[prova] saveProvaActive falhou após retries:", err);
      retryAttempt = 0;
      if (provaWorkspace.value?.bankId === bankId) provaSyncState.value = "error";
      if (backgroundRetryTimer) clearTimeout(backgroundRetryTimer);
      backgroundRetryTimer = setTimeout(() => {
        backgroundRetryTimer = null;
        void flushProvaSave();
      }, 30_000);
    }
  };
  inFlight = doSave().finally(() => {
    inFlight = null;
  });
  await inFlight;
}

// Snapshot da prova com tempos "fechados" até agora — usado em flushes.
function withCurrentTimes(prova: ProvaState): ProvaState {
  if (prova.pausada) return prova;
  const now = Date.now();
  const novoTotal = prova.tempoTotalMs + Math.max(0, now - prova.ultimoRetomadaEm);
  const tpq = { ...prova.tempoPorQuestao };
  if (prova.questaoAtualId && prova.questaoEntrouEm) {
    const diff = Math.max(0, now - prova.questaoEntrouEm);
    tpq[prova.questaoAtualId] = (tpq[prova.questaoAtualId] ?? 0) + diff;
  }
  return {
    ...prova,
    tempoTotalMs: novoTotal,
    tempoPorQuestao: tpq,
    ultimoRetomadaEm: now,
    questaoEntrouEm: now
  };
}

function flushPendingProvaSaveBeforeExit() {
  const ws = provaWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.prova) return;
  const hasPending =
    saveTimer != null ||
    provaSyncState.value === "pending" ||
    provaSyncState.value === "error";
  if (!hasPending) return;
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  const prova = withCurrentTimes(ws.prova);
  void api.saveProvaActive(ws.bankId, prova).catch((err) => {
    console.error("[prova] flush on exit falhou:", err);
  });
}

let unloadBound = false;
export function bindProvaUnloadFlush() {
  if (unloadBound || typeof window === "undefined") return;
  unloadBound = true;
  const onUnload = () => {
    const ws = provaWorkspace.value;
    if (!ws || ws.phase !== "ready" || !ws.prova) return;
    const prova = withCurrentTimes(ws.prova);
    try {
      void fetch("/api/student/bank-prova-active", {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ bankId: ws.bankId, prova }),
        keepalive: true
      }).catch(() => null);
    } catch { /* silencioso */ }
  };
  window.addEventListener("pagehide", onUnload);
  window.addEventListener("beforeunload", onUnload);
}

// =====================
// Tick periódico (refresh cronômetro)
// =====================

let tickInterval: ReturnType<typeof setInterval> | null = null;
let tickBound = false;
export function bindProvaTick() {
  if (tickBound || typeof window === "undefined") return;
  tickBound = true;
  // 500ms é suficiente: a UI mostra HH:MM:SS então não vale gastar frame
  // pra cada 16ms.
  tickInterval = setInterval(() => {
    if (provaWorkspace.value?.prova) {
      _tick.value = _tick.value + 1;
      // Auto-encerra se cronômetro regressivo chegou a 0.
      const restante = getTempoRestanteMs(provaWorkspace.value.prova);
      if (restante != null && restante === 0 && provaWorkspace.value.phase === "ready") {
        void encerrarProva("tempo");
      }
    }
  }, 500);
}

// =====================
// Pausar / Retomar
// =====================
// Pausa cong/desliga os cronômetros. Os tempos atuais são "flushados" pra
// tempoTotalMs/tempoPorQuestao via withCurrentTimes, e pausada=true bloqueia
// a soma do segmento corrente nos getters (linhas 218-232). Retomar reseta
// ultimoRetomadaEm/questaoEntrouEm pra agora, descartando o tempo offline.

export function pausarProvaToggle() {
  const ws = provaWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.prova) return;
  const prova = ws.prova;
  let updated: ProvaState;
  if (prova.pausada) {
    // Retomar: reinicia segmento "agora" sem somar o tempo em pausa.
    const now = Date.now();
    updated = {
      ...prova,
      pausada: false,
      ultimoRetomadaEm: now,
      questaoEntrouEm: prova.questaoAtualId ? now : null
    };
  } else {
    // Pausar: fecha o segmento corrente e marca pausada.
    updated = { ...withCurrentTimes(prova), pausada: true, questaoEntrouEm: null };
  }
  provaWorkspace.value = { ...ws, prova: updated };
  // Agenda save (debounced).
  void api.saveProvaActive(ws.bankId, updated).catch(() => null);
}

// =====================
// Encerrar
// =====================

export async function encerrarProva(motivo: "manual" | "tempo" | "falta-respostas") {
  const ws = provaWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.prova) return;
  // Cancela qualquer save em andamento — o end endpoint já recebe o estado
  // final inteiro, fica autoritativo.
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  provaWorkspace.value = { ...ws, phase: "encerrando" };

  const prova = withCurrentTimes(ws.prova);
  try {
    const res = await api.endProva(ws.bankId, prova, motivo);
    // Atualiza URL pro resultado, nullifica o workspace ativo e abre o
    // ResultView com o snapshot já em mãos (prefilled) — evita uma roundtrip
    // ao /bank-prova-detail. enterProvaResultView fará apenas o fetch das
    // questões do banco.
    const url = `/student/banks/${encodeURIComponent(ws.bankId)}/prova/${encodeURIComponent(
      res.prova.id
    )}/resultado`;
    window.history.pushState({ provaResultId: res.prova.id }, "", url);
    provaWorkspace.value = null;
    enterProvaResultView(ws.bankId, res.prova.id, {
      pushHistory: false,
      prefilled: res.prova
    });
    try { _onExitCallback?.(ws.bankId); } catch { /* ignora */ }
  } catch (err) {
    console.error("[prova] endProva falhou:", err);
    provaWorkspace.value = {
      ...ws,
      phase: "ready",
      errorMsg: err instanceof Error ? err.message : String(err)
    };
  }
}

// =====================
// Keyboard
// =====================

let kbBound = false;
export function bindProvaKeyboard() {
  if (kbBound || typeof window === "undefined") return;
  kbBound = true;
  window.addEventListener("keydown", (e: KeyboardEvent) => {
    const ws = provaWorkspace.value;
    if (!ws || ws.phase !== "ready" || !ws.prova) return;
    // Player aberto consome todos os atalhos.
    if (isPlayerOpen.value) return;
    const t = e.target as HTMLElement | null;
    if (t?.isContentEditable || t?.tagName === "INPUT" || t?.tagName === "TEXTAREA") return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      blurActive();
      gotoPrev();
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      blurActive();
      gotoNext();
      return;
    }
    if (e.key === "Escape") {
      if (provaEncerrarConfirmOpen.value) {
        e.preventDefault();
        provaEncerrarConfirmOpen.value = false;
      }
      return;
    }
    // A-E ou 1-5 marca alternativa (mesma regra do estudo).
    const escopo = filterEscopo(ws.data?.questoes ?? [], ws.prova.escopoQuestaoIds);
    const q = escopo[ws.currentIdx];
    if (!q) return;
    if (/^[a-eA-E]$/.test(e.key)) {
      const idx = e.key.toUpperCase().charCodeAt(0) - 65;
      if (idx < q.opcoes.length) {
        e.preventDefault();
        setRespostaProva(idx);
      }
      return;
    }
    if (/^[1-5]$/.test(e.key)) {
      const idx = Number(e.key) - 1;
      if (idx < q.opcoes.length) {
        e.preventDefault();
        setRespostaProva(idx);
      }
      return;
    }
    // 0 → limpar resposta da questão atual (atalho do "Limpar resposta").
    if (e.key === "0") {
      e.preventDefault();
      setRespostaProva(null);
      return;
    }
  });
}

function blurActive() {
  if (typeof document === "undefined") return;
  const el = document.activeElement as HTMLElement | null;
  if (el && (el.tagName === "BUTTON" || el.tagName === "A")) el.blur();
}

// =========================================================================
// UI
// =========================================================================

export function ProvaWorkspaceView() {
  const ws = provaWorkspace.value;
  if (!ws) return null;

  if (ws.phase === "loading") return <ProvaLoading progress={ws.loadProgress ?? null} />;
  if (ws.phase === "error") return <ProvaError msg={ws.errorMsg} />;
  if (ws.phase === "encerrando") return <ProvaEncerrando />;
  // ready (sem prova ainda) cai pra placeholder — o App.tsx vai abrir o
  // ProvaStartModal por cima desse view.
  if (!ws.prova) return <ProvaSemAtiva />;
  return <ProvaReady ws={ws} />;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ProvaLoadProgressBanner({ progress }: { progress: FetchProgress | null }) {
  if (!progress) return null;
  const pct =
    progress.total && progress.total > 0
      ? Math.min(100, Math.round((progress.loaded / progress.total) * 100))
      : null;
  const totalLabel = progress.total ? formatBytes(progress.total) : "calculando";
  return (
    <div class="rounded-md border border-violet-500/40 bg-violet-500/5 p-4">
      <div class="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
        <div class="font-medium text-violet-700 dark:text-violet-300">
          Baixando a prova…
        </div>
        <div class="tabular-nums text-muted-foreground">
          {formatBytes(progress.loaded)} / {totalLabel}
          {pct != null ? ` · ${pct}%` : ""}
        </div>
      </div>
      <div class="h-2 w-full overflow-hidden rounded-full bg-violet-500/15">
        <div
          class="h-full rounded-full bg-violet-500 transition-[width] duration-150 ease-out"
          style={{ width: pct != null ? `${pct}%` : "10%" }}
        />
      </div>
      {progress.total && progress.total > 3 * 1024 * 1024 ? (
        <p class="mt-2 text-xs text-muted-foreground">
          Esse banco tem imagens grandes (≈ {formatBytes(progress.total)}). Em conexões
          móveis pode levar alguns segundos — não recarregue.
        </p>
      ) : null}
    </div>
  );
}

function ProvaLoading({ progress }: { progress: FetchProgress | null }) {
  // Espelha o layout REAL do ProvaWorkspaceView ativo:
  //   - Header em 2 linhas (identidade + contador + ações | 4 cronômetros)
  //   - Card grande de questão (3 badges pequenas + enunciado 3-4 linhas + 5 alternativas)
  //   - Navegação inferior simples (Anterior | Próxima)
  // Sem mapa/lista lateral (ainda não portados).
  return (
    <div class="space-y-4">
      <ProvaLoadProgressBanner progress={progress} />
      {/* Header — espelha ProvaHeader */}
      <header class="space-y-3 rounded-md border border-border bg-card/60 p-4">
        {/* Linha 1: ícone + título + contador destaque + 2 botões */}
        <div class="flex flex-wrap items-center gap-3">
          <Skeleton className="size-10 shrink-0 rounded-md" />
          <div class="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-72 max-w-full" />
            <Skeleton className="h-3 w-44" />
          </div>
          <Skeleton className="h-[68px] w-40 shrink-0 rounded-md" />
          <Skeleton className="h-10 w-28 shrink-0 rounded-md" />
          <Skeleton className="h-10 w-28 shrink-0 rounded-md" />
        </div>
        {/* Linha 2: cronômetros (4 chips) */}
        <div class="flex flex-wrap gap-2">
          <Skeleton className="h-[52px] w-28 rounded-md" />
          <Skeleton className="h-[52px] w-28 rounded-md" />
          <Skeleton className="h-[52px] w-24 rounded-md" />
          <Skeleton className="h-[52px] w-44 rounded-md" />
        </div>
      </header>

      {/* Card de questão */}
      <div class="space-y-5 rounded-md border border-border bg-card/40 p-5">
        {/* Badges (Q + tema + dificuldade) */}
        <div class="flex flex-wrap gap-1.5">
          <Skeleton className="h-5 w-7 rounded-full" />
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-12 rounded-full" />
        </div>
        {/* Enunciado (3-4 linhas longas) */}
        <div class="space-y-2.5">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-10/12" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        {/* Linha "A hipótese diagnóstica mais provável é" */}
        <Skeleton className="h-4 w-72" />
        {/* Alternativas (5, com letra-pill à esquerda) */}
        <ul class="space-y-3 pt-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <li
              key={i}
              class="flex items-center gap-3 rounded-md border border-border bg-background/60 px-4 py-3"
            >
              <Skeleton className="size-7 shrink-0 rounded-full" />
              <Skeleton className="h-4 flex-1" />
            </li>
          ))}
        </ul>
        {/* "Limpar resposta" à direita */}
        <div class="flex justify-end">
          <Skeleton className="h-3.5 w-24" />
        </div>
      </div>

      {/* Navegação inferior (Anterior | Próxima) */}
      <div class="flex items-center justify-between gap-3 rounded-md border border-border bg-card/40 px-4 py-3">
        <Skeleton className="h-9 w-28 rounded-md" />
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>
    </div>
  );
}

function ProvaError({ msg }: { msg: string | null }) {
  return (
    <div class="grid place-items-center py-16">
      <div class="max-w-md space-y-3 rounded-md border border-destructive/30 bg-destructive/5 px-6 py-8 text-center">
        <AlertCircle className="mx-auto size-8 text-destructive" />
        <h2 class="text-base font-bold">Não consegui carregar a simulação</h2>
        {msg && <p class="text-sm text-muted-foreground">{msg}</p>}
        <button
          type="button"
          onClick={() => {
            const bankId = provaWorkspace.value?.bankId ?? null;
            exitProvaWorkspace();
            if (bankId) {
              activeNav.value = "bancos";
              openBank(bankId);
            }
          }}
          class="mt-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Voltar para o banco
        </button>
      </div>
    </div>
  );
}

function ProvaEncerrando() {
  // Servidor faz auto-correção IA das essays/items após salvar — pode
  // levar 30s-2min em provas dissertativas longas (1 chamada por item).
  // Aviso explícito pro aluno não fechar a aba. Karol 2026-05-24.
  return (
    <div class="grid place-items-center py-16">
      <div class="max-w-md rounded-md border border-border bg-card/60 px-6 py-8 text-center">
        <Flag className="mx-auto mb-3 size-8 animate-pulse text-primary" />
        <h2 class="text-base font-bold">Encerrando e corrigindo com IA…</h2>
        <p class="mt-1 text-sm text-muted-foreground">
          Salvando suas respostas e disparando a correção automática das
          questões dissertativas. Pode levar de 30 segundos a 2 minutos.
        </p>
        <p class="mt-2 text-xs italic text-muted-foreground">
          Não feche essa aba — a nota e o feedback de cada questão vão
          aparecer no resultado.
        </p>
      </div>
    </div>
  );
}

function ProvaSemAtiva() {
  // Mostrado enquanto o ProvaStartModal carrega — o modal vai abrir por
  // cima desse view, então é só um esqueleto neutro.
  return (
    <div class="grid place-items-center py-16">
      <div class="max-w-md rounded-md border border-dashed border-border bg-card/40 px-6 py-8 text-center text-sm text-muted-foreground">
        Configure sua simulação para começar.
      </div>
    </div>
  );
}

function ProvaReady({ ws }: { ws: ProvaWorkspaceState }) {
  const prova = ws.prova!;
  const data = ws.data!;
  // Bucket de essay SCOPED na prova (Karol 2026-05-24: não misturar com
  // Study). Hidratação:
  //   1. Se prova.essayResponses tem entries → usa direto (prova já tinha
  //      progresso aqui)
  //   2. Senão → vazio (prova nova começa sem memória do Study)
  // Não mais lê de `data.essayResponses` (Study bucket).
  const [essayResponses, setEssayResponses] = useState<
    Record<string, EssayResponseState>
  >(() => prova.essayResponses ?? {});
  function onEssayChange(key: string, state: EssayResponseState) {
    setEssayResponses((prev) => {
      const next = { ...prev, [key]: state };
      // Persiste a essayResponses INTEIRA no prova-state (snapshot vai pro
      // PUT /bank-prova-active na próxima save). Sem isso, refresh durante
      // prova perderia respostas dissertativas.
      const cur = provaWorkspace.value;
      if (cur?.prova) {
        const touched = Array.isArray(cur.prova.essayKeysTouched)
          ? cur.prova.essayKeysTouched
          : [];
        const newTouched = touched.includes(key) ? touched : [...touched, key];
        const updated = {
          ...cur.prova,
          essayResponses: next,
          essayKeysTouched: newTouched
        };
        provaWorkspace.value = { ...cur, prova: updated };
        scheduleProvaSave();
      }
      return next;
    });
  }
  // Lista visível = escopo + filtros (filtros via signal provaFilters).
  // Subscribe ao signal pra re-renderizar quando filtros mudarem.
  // Subscribe ao tick — garante re-render a cada 500ms enquanto a prova
  // está rodando. Sem isso, os badges de tempo (na lista e no QuestionCard)
  // ficam estáticos: tempoQuestaoAtualMs() é função pura que depende do
  // relógio, então a UI só atualizaria em mudanças de outros sinais.
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  _tick.value;
  const filters = provaFilters.value;
  const escopoTotal = filterEscopo(data.questoes, prova.escopoQuestaoIds).length;
  // Set imutável das keys tocadas — derivado a cada render pra evitar criar
  // novo Set toda chamada interna ao isProvaRespondida.
  const essayKeysTouchedSet = useMemo(
    () => new Set(prova.essayKeysTouched ?? []),
    [prova.essayKeysTouched]
  );
  const visiveis = getProvaVisiveis(
    data.questoes,
    prova.escopoQuestaoIds,
    filters,
    prova.respostas,
    essayResponses,
    essayKeysTouchedSet
  );
  // currentIdx é índice dentro do escopo completo; se a questão atual
  // sumiu pelo filtro, o usuário ainda a vê (não muda automaticamente).
  // Pra navegação: gotoNext/Prev pulam só entre as visíveis.
  const escopo = filterEscopo(data.questoes, prova.escopoQuestaoIds);
  const q = escopo[ws.currentIdx];
  const selected = q ? prova.respostas[q.id]?.opcao : undefined;
  const ativos = countActiveProvaFilters(filters);
  // Contagem unificada de "respondidas" — inclui MC com opcao marcada,
  // dissertativas com texto digitado NESTA PROVA, e anuladas. Karol 2026-05-24:
  // SEM essayKeysTouched, contava bucket compartilhado com Study e dava 3-7
  // mesmo prova recém-iniciada.
  const respondidasCount = escopo.reduce(
    (acc, qq) =>
      acc +
      (isProvaRespondida(
        qq,
        prova.respostas,
        essayResponses,
        essayKeysTouchedSet
      )
        ? 1
        : 0),
    0
  );
  // Side da lista controlado pela preferência do aluno (uiPrefs). Como a
  // lista é o SEGUNDO filho do flex, a lógica inverte em relação ao Study.
  const listSide = uiPrefs.value.questionListSide;
  return (
    <div class="space-y-4">
      <ProvaHeader
        prova={prova}
        bank={data.bank}
        currentIdx={ws.currentIdx}
        total={escopoTotal}
        visiveis={visiveis.length}
        filtrosAtivos={ativos}
        respondidasCount={respondidasCount}
      />
      <div
        class={`flex flex-col gap-4 lg:items-start ${
          listSide === "right" ? "lg:flex-row-reverse" : "lg:flex-row"
        }`}
      >
        <div class="min-w-0 flex-1 space-y-4">
          {q ? (
            <QuestionCard
              q={q}
              selected={selected}
              onSelect={(i) => setRespostaProva(i)}
              onClear={() => setRespostaProva(null)}
              feedback="none"
              hideTema={prova.config.ocultarTema}
              hideDificuldade={prova.config.ocultarDificuldade}
              tempoMs={getTempoQuestaoAtualMs(prova)}
              bankId={ws.bankId}
              essayResponses={essayResponses}
              onEssayChange={onEssayChange}
              provaMode
            />
          ) : (
            <div class="rounded-md border border-dashed border-border bg-card/40 p-10 text-center text-sm text-muted-foreground">
              Escopo vazio.
            </div>
          )}
          <ProvaNavigation
            currentIdx={ws.currentIdx}
            total={escopoTotal}
            onPrev={gotoPrev}
            onNext={gotoNext}
          />
        </div>
      {/* Lista lateral só em desktop (lg+). Mesmo componente do StudyWorkspace,
          só muda o statusOf (em prova: respondida/pendente/anulada, sem
          correct/wrong porque o aluno não vê acerto durante a prova). */}
      {(() => {
        // A sidebar trabalha com a lista ORDENADA por tema/fonte. O workspace,
        // por outro lado, navega no escopo original (não ordenado) via
        // currentIdx. Precisamos mapear:
        //   - `currentIdx` na sidebar = posição da questão ATUAL (escopo[currentIdx])
        //                                dentro da lista ordenada
        //   - onPick(sortedIdx) → encontra a questão ordenada[sortedIdx],
        //                         localiza no escopo unsorted e chama gotoIdx.
        const sortedQuestoes = applySharedAgrupamento(escopo, provaAgrupamento.value);
        const currentQ = escopo[ws.currentIdx];
        const sortedCurrentIdx = currentQ
          ? sortedQuestoes.findIndex((sq) => sq.id === currentQ.id)
          : 0;
        // Número estável (ordem FONTE) — escopo é a ordem original da prova.
        // Q42 fica Q42 mesmo após sort/filtro.
        const numByIdMap = new Map<string, number | string>();
        escopo.forEach((eq: any, i: number) => {
          const n = eq?.numero_original;
          numByIdMap.set(String(eq.id), n != null && n !== "" ? n : i + 1);
        });
        const numOfQ = (q: any) => numByIdMap.get(String(q.id)) ?? "?";
        return (
          <QuestionListSidebar
            questoes={sortedQuestoes}
            currentIdx={Math.max(0, sortedCurrentIdx)}
            numOf={numOfQ}
            onPick={(sortedIdx) => {
              const targetQ = sortedQuestoes[sortedIdx];
              if (!targetQ) return;
              const unsortedIdx = escopo.findIndex((eq) => eq.id === targetQ.id);
              if (unsortedIdx >= 0) gotoIdx(unsortedIdx);
            }}
            statusOf={(q) => {
              if (isAnulada(q)) return "anulada";
              // Sidebar usa MESMA fonte de verdade que o counter do header
              // (isProvaRespondida) — antes divergia: header=3 sidebar=7 em
              // banco com bucket compartilhado com Study. Fix Karol 2026-05-24.
              return isProvaRespondida(
                q,
                prova.respostas,
                essayResponses,
                essayKeysTouchedSet
              )
                ? "answered"
                : "pending";
            }}
            tempoMsOf={(q) => {
              // Pra questão ATIVA, soma o tempo decorrido desde que entrou
              // (questaoEntrouEm). Pra outras, usa o acumulado em tempoPorQuestao
              // ou o snapshot salvo no momento da resposta. Sem isso, a questão
              // ativa fica com badge zerada até o aluno navegar pra outra.
              if (q.id === prova.questaoAtualId) {
                return getTempoQuestaoAtualMs(prova);
              }
              return (
                prova.tempoPorQuestao?.[q.id] ??
                prova.respostas[q.id]?.tempoMs ??
                0
              );
            }}
            agrupamento={provaAgrupamento.value}
            onAgrupamentoChange={(a) => (provaAgrupamento.value = a)}
            visibleIds={new Set(visiveis.map((q) => String(q.id)))}
            countLabel={`${visiveis.length}/${escopo.length}`}
          />
        );
      })()}
      </div>
      {provaFilterPanelOpen.value && (
        <ProvaFilterPanel
          questoes={escopo}
          respostas={prova.respostas}
          essayResponses={essayResponses}
          essayKeysTouched={essayKeysTouchedSet}
        />
      )}
      {provaMapOpen.value && (
        <ProvaMapModal
          questoes={escopo}
          respostas={prova.respostas}
          essayResponses={essayResponses}
          essayKeysTouched={essayKeysTouchedSet}
          currentIdx={ws.currentIdx}
          onPick={(targetIdx) => {
            gotoIdx(targetIdx);
            provaMapOpen.value = false;
          }}
        />
      )}
      {provaListMobileOpen.value && (
        <ProvaListMobileModal
          questoes={applySharedAgrupamento(escopo, provaAgrupamento.value)}
          currentIdx={ws.currentIdx}
          escopo={escopo}
          respostas={prova.respostas}
          essayResponses={essayResponses}
          essayKeysTouched={essayKeysTouchedSet}
          visibleIds={new Set(visiveis.map((q) => String(q.id)))}
          countLabel={`${visiveis.length}/${escopo.length}`}
          agrupamento={provaAgrupamento.value}
          onAgrupamentoChange={(a) => (provaAgrupamento.value = a)}
          onPick={(targetIdx) => {
            gotoIdx(targetIdx);
            provaListMobileOpen.value = false;
          }}
        />
      )}
      {provaEncerrarConfirmOpen.value && (
        <ProvaEncerrarConfirm
          respondidas={respondidasCount}
          total={escopoTotal}
        />
      )}
      {prova.pausada && <ProvaPausadaModal />}
      {lightboxImg.value && <Lightbox image={lightboxImg.value} />}
    </div>
  );
}

// =====================
// Header com cronômetros + ações + contador em destaque
// =====================

function ProvaHeader({
  prova,
  bank,
  currentIdx,
  total,
  visiveis,
  filtrosAtivos,
  respondidasCount
}: {
  prova: ProvaState;
  bank: BankProvaResponse["bank"];
  currentIdx: number;
  total: number;
  visiveis: number;
  filtrosAtivos: number;
  /** Override do contador interno — passado pelo ProvaReady com base em
   *  isProvaRespondida (inclui dissertativas com texto). Se undefined,
   *  cai no fallback (Object.keys das respostas MC), que sub-conta. */
  respondidasCount?: number;
}) {
  // Subscribe ao tick — garante re-render a cada 500ms enquanto os
  // cronômetros estão rodando, sem depender de eventos do usuário.
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  _tick.value;
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  provaTimerModes.value;
  const restante = getTempoRestanteMs(prova);
  const decorrido = getTempoDecorridoMs(prova);
  const tempoQ = getTempoQuestaoAtualMs(prova);
  const alvo = prova.config.tempoAlvoMs;
  const respondidas = respondidasCount ?? Object.keys(prova.respostas).length;
  const pct = total > 0 ? Math.min(100, Math.round((respondidas / total) * 100)) : 0;
  const hasGeral = prova.config.mostrarRelogioGeral;
  const hasMaximo = prova.config.mostrarRelogioGeral && alvo > 0;
  const hasAtual = prova.config.mostrarRelogioQuestao;
  const hasMedia = prova.config.mostrarRelogioRitmo !== false;
  const geralLabel = prova.config.regressivo ? "Restante" : "Decorrido";
  const geralTooltip = prova.config.regressivo
    ? "Tempo restante até o fim"
    : "Tempo total decorrido";
  const geralValue = formatHMS(
    prova.config.regressivo && restante != null ? restante : decorrido
  );
  const geralWarning =
    prova.config.regressivo && restante != null && restante < 60_000;

  // Modo compacto: bandeira + tempo restante + expandir. Maximiza espaço
  // pra questão, que é o foco. Toggle persiste em uiPrefs.
  if (uiPrefs.value.provaHeaderCompact) {
    return (
      <header class="flex items-center gap-2 rounded-md border border-border bg-card/60 px-3 py-2">
        <div class="grid size-8 shrink-0 place-items-center rounded-md bg-amber-500/15 text-amber-600 ring-1 ring-amber-500/30 dark:text-amber-400">
          <Flag className="size-4" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="truncate text-xs font-semibold leading-tight">
            {bank.name}
          </div>
          <div class="text-[10px] leading-tight text-muted-foreground">
            Q{currentIdx + 1}/{total} · {respondidas} respondidas
          </div>
        </div>
        {hasGeral && (
          <div
            class={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm font-bold tabular-nums leading-none ${
              geralWarning
                ? "border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400"
                : "border-primary/40 bg-primary/10 text-primary"
            }`}
            title={geralTooltip}
          >
            {prova.config.regressivo ? (
              <TimerReset width="0.95em" height="0.95em" />
            ) : (
              <Clock width="0.95em" height="0.95em" />
            )}
            {geralValue}
          </div>
        )}
        <button
          type="button"
          onClick={toggleProvaHeaderCompact}
          title="Expandir cabeçalho"
          aria-label="Expandir cabeçalho da prova"
          class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ChevronDown className="size-4" />
        </button>
      </header>
    );
  }

  // Layout unificado via WorkspaceHeader. Summary = gauge de respondidas
  // com barra de progresso; timers = 4 chips clicáveis com ciclo de modo;
  // ações em grid 2 col / 3 col conforme o breakpoint. Banner de pausa
  // mora fora do header (renderizado pelo caller) — antes ficava grudado.
  const actions: WorkspaceHeaderAction[] = [
    {
      id: "mapa",
      icon: Grid3x3,
      label: "Mapa",
      title: "Mapa de questões",
      onClick: () => (provaMapOpen.value = true)
    },
    {
      id: "lista",
      icon: ListIcon,
      label: "Lista",
      title: uiPrefs.value.questionListCollapsed
        ? "Mostrar lista de questões"
        : "Esconder lista de questões",
      onClick: () => {
        if (window.matchMedia("(max-width: 1023px)").matches) {
          provaListMobileOpen.value = true;
        } else {
          toggleQuestionListCollapsed();
        }
      },
      tone: uiPrefs.value.questionListCollapsed ? "amber" : "neutral"
    },
    {
      id: "filtros",
      icon: SlidersHorizontal,
      label: "Filtros",
      title:
        filtrosAtivos > 0
          ? `${filtrosAtivos} filtro(s) ativos — ${visiveis} de ${total} visíveis`
          : "Filtros",
      onClick: () => (provaFilterPanelOpen.value = true),
      tone: filtrosAtivos > 0 ? "amber" : "neutral",
      badge: filtrosAtivos > 0 ? String(filtrosAtivos) : undefined
    },
    {
      id: "pausar",
      icon: prova.pausada ? Play : Pause,
      label: prova.pausada ? "Retomar" : "Pausar",
      title: prova.pausada ? "Retomar prova" : "Pausar prova",
      onClick: pausarProvaToggle,
      // Pausar é uma ação "perigosa-leve" — sempre âmbar pra destacar,
      // mesmo quando ainda não tem prova pausada.
      tone: "amber"
    },
    {
      id: "encerrar",
      icon: Flag,
      label: "Encerrar",
      title: "Encerrar prova",
      onClick: () => (provaEncerrarConfirmOpen.value = true),
      tone: "danger"
    }
  ];

  return (
    <>
      <WorkspaceHeader
        mode="prova"
        icon={Flag}
        iconTone="amber"
        title={bank.name}
        subtitle={
          <>
            Questão {currentIdx + 1} de {total}
            <span class="mx-1.5 opacity-50">·</span>
            Simulação de Prova
          </>
        }
        summary={
          <RespondidasGauge respondidas={respondidas} total={total} pct={pct} />
        }
        timers={
          <ProvaTimersRow
            hasGeral={hasGeral}
            hasMaximo={hasMaximo}
            hasAtual={hasAtual}
            hasMedia={hasMedia}
            geralLabel={geralLabel}
            geralTooltip={geralTooltip}
            geralValue={geralValue}
            geralWarning={geralWarning}
            regressivo={prova.config.regressivo}
            alvo={alvo}
            tempoQ={tempoQ}
            restante={restante}
            prova={prova}
          />
        }
        actions={actions}
        compactToggle={{
          compact: false,
          onClick: toggleProvaHeaderCompact
        }}
      />
      {prova.pausada && (
        <div class="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-300">
          <Pause width="1em" height="1em" />
          Prova pausada — os cronômetros pararam. Clique em Retomar para
          continuar.
        </div>
      )}
    </>
  );
}

// Gauge "Respondidas X / total" com barra de progresso. Slot summary do
// WorkspaceHeader nos modos prova / prova-agendada.
function RespondidasGauge({
  respondidas,
  total,
  pct
}: {
  respondidas: number;
  total: number;
  pct: number;
}) {
  return (
    <div class="flex h-full min-w-[160px] flex-col justify-center rounded-md border border-primary/30 bg-primary/5 px-3.5 py-3 leading-tight">
      <span class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Respondidas
      </span>
      <span class="tabular-nums text-xl font-bold text-foreground lg:text-2xl">
        {respondidas} <span class="text-muted-foreground">/ {total}</span>
      </span>
      <div class="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background">
        <div
          class="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
          data-pct={pct}
          ref={(el) => {
            if (el) (el as HTMLDivElement).style.width = `${pct}%`;
          }}
        />
      </div>
    </div>
  );
}

// Linha de cronômetros para o slot `timers` do WorkspaceHeader. Reúne os
// 4 chips clicáveis que existiam no desktop, mantendo a mesma lógica de
// ciclo (normal → âmbar → oculto).
function ProvaTimersRow({
  hasGeral,
  hasMaximo,
  hasAtual,
  hasMedia,
  geralLabel,
  geralTooltip,
  geralValue,
  geralWarning,
  regressivo,
  alvo,
  tempoQ,
  restante,
  prova
}: {
  hasGeral: boolean;
  hasMaximo: boolean;
  hasAtual: boolean;
  hasMedia: boolean;
  geralLabel: string;
  geralTooltip: string;
  geralValue: string;
  geralWarning: boolean;
  regressivo: boolean;
  alvo: number;
  tempoQ: number;
  restante: number | null;
  prova: ProvaState;
}) {
  return (
    <>
      {hasGeral && (
        <ClickableTimerChip
          tkey="geral"
          label={geralLabel}
          tooltip={geralTooltip}
          icon={regressivo ? TimerReset : Clock}
          value={geralValue}
          warning={geralWarning}
          emphasis="primary"
        />
      )}
      {hasMaximo && (
        <ClickableTimerChip
          tkey="maximo"
          label="Máximo"
          tooltip="Tempo total definido para a prova"
          icon={Hourglass}
          value={formatHMS(alvo)}
        />
      )}
      {hasAtual && (
        <ClickableTimerChip
          tkey="atual"
          label="Atual"
          tooltip="Tempo gasto na questão atual"
          icon={Clock}
          value={formatHMS(tempoQ)}
        />
      )}
      {hasMedia && (
        <ClickableTimerChip
          tkey="media"
          label="Média p/ restantes"
          tooltip="Quanto tempo, em média, sobra por questão que você ainda não respondeu (tempo restante ÷ não respondidas)"
          icon={Timer}
          value={computeRitmoLabel(prova, restante)}
        />
      )}
    </>
  );
}

// Cronômetro em formato de "célula de grid" pra mobile: sem ícone, label
// em uppercase pequeno em cima e valor grande embaixo. Mantém o ciclo de
// modo (normal → âmbar → oculto) como no chip horizontal do desktop.
function MobileTimerCell({
  tkey,
  label,
  tooltip,
  value,
  warning,
  emphasis
}: {
  tkey: TimerKey;
  label: string;
  tooltip?: string;
  value: string;
  warning?: boolean;
  emphasis?: "primary";
}) {
  const mode = provaTimerModes.value[tkey] || "normal";
  const isAmber = mode === "amber";
  const isHidden = mode === "hidden";
  const tone = isHidden
    ? "border-dashed border-border bg-background/40 text-muted-foreground"
    : warning
      ? "border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400 animate-pulse"
      : isAmber
        ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400"
        : emphasis === "primary"
          ? "border-primary/40 bg-primary/5 text-foreground"
          : "border-border bg-background text-foreground/85";
  const nextHint =
    mode === "normal" ? "destacar em âmbar" : mode === "amber" ? "ocultar valor" : "mostrar valor";
  return (
    <button
      type="button"
      onClick={() => cycleTimerMode(tkey)}
      title={`${tooltip || label} — clique para ${nextHint}`}
      class={`flex flex-col items-start rounded-md border px-3 py-2 text-left leading-tight transition-colors hover:brightness-[0.97] ${tone}`}
    >
      <span class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {isHidden ? (
        <span
          class="select-none text-lg font-bold tabular-nums tracking-[0.18em] text-muted-foreground/70"
          aria-label="valor oculto"
        >
          •••
        </span>
      ) : (
        <span class="text-lg font-bold tabular-nums">{value}</span>
      )}
    </button>
  );
}

// Botão de ação do header com tamanho fixo, para que Pausar/Encerrar
// fiquem do mesmo tamanho independentemente do texto. Em mobile, só
// ícone (label fica em sr-only/title).
function HeaderActionButton({
  onClick,
  title,
  icon: Icon,
  label,
  tone,
  badge
}: {
  onClick: () => void;
  title: string;
  icon: typeof Pause;
  label: string;
  tone: "neutral" | "amber" | "danger";
  badge?: string;
}) {
  const cls =
    tone === "amber"
      ? "border-amber-500/50 bg-amber-500/10 text-amber-700 hover:bg-amber-500/15 dark:text-amber-400"
      : tone === "danger"
        ? "border-red-500/40 bg-red-500/5 text-red-600 hover:bg-red-500/10 dark:text-red-400"
        : "border-border bg-background text-foreground hover:bg-accent";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      class={`relative inline-flex h-10 items-center justify-center gap-1.5 rounded-md border px-2.5 text-sm font-medium transition-colors md:min-w-[6.5rem] md:px-3 ${cls}`}
    >
      <Icon width="1.1em" height="1.1em" />
      <span class="hidden md:inline">{label}</span>
      {badge && (
        <span class="absolute -right-1.5 -top-1.5 grid min-w-[18px] place-items-center rounded-full bg-amber-500 px-1 text-[10px] font-bold leading-none text-white shadow ring-2 ring-card">
          {badge}
        </span>
      )}
    </button>
  );
}

// "Média p/ restantes" = tempo restante ÷ questões não respondidas. Sem
// tempo máximo definido (progressivo livre) → "Não Definido". Sem questões
// pendentes ou tempo esgotado → "—". Atualiza a cada tick.
function computeRitmoLabel(prova: ProvaState, restanteMs: number | null): string {
  if (!prova.config.regressivo || restanteMs === null) return "Não Definido";
  if (restanteMs <= 0) return "—";
  const ids = Array.isArray(prova.escopoQuestaoIds) ? prova.escopoQuestaoIds : [];
  const respostas = prova.respostas || {};
  let naoRespondidas = 0;
  for (const qid of ids) {
    const r = respostas[qid];
    if (!r || r.opcao == null) naoRespondidas++;
  }
  if (naoRespondidas <= 0) return "—";
  return formatHMS(Math.floor(restanteMs / naoRespondidas));
}

// Cronômetro clicável com cycle normal → âmbar → oculto. No modo "hidden",
// a caixa, ícone e título são mantidos; só o valor numérico vira "•••"
// (intencional — comunica "está rodando mas escondido", e o usuário pode
// destacar de volta com um clique).
function ClickableTimerChip({
  tkey,
  label,
  tooltip,
  icon: Icon,
  value,
  warning,
  emphasis
}: {
  tkey: TimerKey;
  label: string;
  tooltip?: string;
  icon: typeof Clock;
  value: string;
  warning?: boolean;
  emphasis?: "primary";
}) {
  const mode = provaTimerModes.value[tkey] || "normal";
  const isAmber = mode === "amber";
  const isHidden = mode === "hidden";
  const tone = isHidden
    ? "border-dashed border-border bg-background/40 text-muted-foreground"
    : warning
      ? "border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400 animate-pulse"
      : isAmber
        ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400"
        : emphasis === "primary"
          ? "border-primary/40 bg-primary/5 text-foreground"
          : "border-border bg-background text-foreground/85";
  const nextHint =
    mode === "normal" ? "destacar em âmbar" : mode === "amber" ? "ocultar valor" : "mostrar valor";
  // Altura fixa h-[88px] = 2 botões h-10 + gap-2 (40+8+40). Match visual
  // com o grid de ações ao lado. Largura fixa pra que labels longos
  // ("MÉDIA P/ RESTANTES") quebrem em 2 linhas no lugar de espremer.
  return (
    <button
      type="button"
      onClick={() => cycleTimerMode(tkey)}
      title={`${tooltip || label} — clique para ${nextHint}`}
      class={`flex h-[88px] w-[136px] flex-col items-start justify-center gap-1.5 rounded-md border px-3 py-2 tabular-nums transition-colors hover:brightness-[0.97] ${tone}`}
    >
      <span class="flex w-full items-start gap-1.5 text-[10px] font-semibold uppercase leading-[1.15] tracking-wider text-muted-foreground">
        <Icon
          width="1em"
          height="1em"
          className={`mt-px shrink-0 ${warning || isAmber ? "" : "text-muted-foreground"}`}
        />
        <span class="min-w-0 flex-1 whitespace-normal break-words text-left">
          {label}
        </span>
      </span>
      {isHidden ? (
        <span
          class="select-none text-2xl font-bold tracking-[0.18em] text-muted-foreground/70"
          aria-label="valor oculto"
        >
          •••
        </span>
      ) : (
        <span class="text-2xl font-bold leading-none">{value}</span>
      )}
    </button>
  );
}

// =====================
// Questão (sem feedback)
// =====================

function ProvaQuestionView({
  q,
  selected,
  config
}: {
  q: QuestaoStudy;
  selected: number | undefined;
  config: ProvaConfig;
}) {
  return (
    <div class="space-y-4 rounded-md border border-border bg-card/40 p-5">
      {/* Badges padronizados (mesmo padrão do modo Estudo). Em prova, sem
          feedback de acerto/erro — só metadados, tipo (caso/imagem/tabela)
          e anulada. Respeita ocultarTema/ocultarDificuldade da config. */}
      <QuestionBadges
        q={q}
        anulada={isAnulada(q)}
        ocultarTema={config.ocultarTema}
        ocultarDificuldade={config.ocultarDificuldade}
      />
      {/* Situação (caso clínico) — renderizado com markdown */}
      {q.caso && (
        <div class="rounded-md border-l-4 border-sky-500/50 bg-sky-500/5 px-4 py-3 text-sm">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400">
            Situação
          </div>
          <div
            class="prose-question text-foreground/85"
            dangerouslySetInnerHTML={{ __html: renderBlock(q.caso) }}
          />
        </div>
      )}

      {/* Tabelas (opcional) */}
      {Array.isArray(q.tabelas) && q.tabelas.length > 0 && (
        <div class="space-y-3">
          {q.tabelas.map((t, i) => (
            <QuestionTable key={i} table={t} />
          ))}
        </div>
      )}

      {/* Imagens (opcional, com lightbox ao clicar) */}
      {(() => {
        const imgs: Array<{ url: string; legenda?: string }> = [];
        if (typeof q.imagem_url === "string" && q.imagem_url) {
          imgs.push({ url: q.imagem_url });
        }
        if (Array.isArray(q.imagens)) {
          for (const it of q.imagens) {
            if (typeof it === "string") imgs.push({ url: it });
            else if (it && typeof it === "object" && typeof it.url === "string") {
              imgs.push({ url: it.url, legenda: it.legenda });
            }
          }
        }
        if (imgs.length === 0) return null;
        const single = imgs.length === 1;
        return (
          <div
            class={`mx-auto grid gap-3 ${
              single ? "max-w-md" : "max-w-2xl sm:grid-cols-2"
            }`}
          >
            {imgs.map((im, i) => (
              <figure
                key={i}
                class="overflow-hidden rounded-md border border-border bg-accent/20"
              >
                <button
                  type="button"
                  onClick={() => (lightboxImg.value = im)}
                  class="block w-full text-left transition-opacity hover:opacity-90"
                  title="Clique pra ampliar"
                >
                  <img
                    src={im.url}
                    alt={im.legenda ?? `Imagem ${i + 1}`}
                    class="mx-auto block w-full cursor-zoom-in"
                    loading="lazy"
                  />
                </button>
                {im.legenda && (
                  <figcaption
                    class="border-t border-border bg-card/60 px-3 py-2 text-center text-xs text-muted-foreground"
                    dangerouslySetInnerHTML={{ __html: renderInline(im.legenda) }}
                  />
                )}
              </figure>
            ))}
          </div>
        );
      })()}

      {/* Pergunta com markdown */}
      <div
        class="prose-question text-base leading-relaxed text-foreground"
        dangerouslySetInnerHTML={{ __html: renderBlock(q.pergunta) }}
      />
      {/* Alternativas — visual NEUTRO (sem cor de acerto/erro) */}
      <ul class="space-y-2">
        {q.opcoes.map((opt, i) => {
          const letter = String.fromCharCode(65 + i);
          const isSelected = selected === i;
          return (
            <li key={i}>
              <button
                type="button"
                onClick={() => setRespostaProva(i)}
                aria-pressed={isSelected}
                class={`flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left text-sm transition-colors ${
                  isSelected
                    ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                    : "border-border bg-card/60 hover:bg-accent/60"
                }`}
              >
                <span
                  class={`grid size-7 shrink-0 place-items-center rounded-full border text-xs font-bold ${
                    isSelected
                      ? "border-primary bg-primary text-white"
                      : "border-border bg-background"
                  }`}
                >
                  {letter}
                </span>
                <span class="flex-1 leading-relaxed">{opt}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {selected != null && (
        <div class="flex items-center justify-end">
          <button
            type="button"
            onClick={() => setRespostaProva(null)}
            class="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
          >
            Limpar resposta
          </button>
        </div>
      )}
    </div>
  );
}

// =====================
// Navegação inferior (Pausar/Encerrar/contador agora vivem no header)
// =====================

function ProvaNavigation({
  currentIdx,
  total,
  onPrev,
  onNext
}: {
  currentIdx: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const atFirst = currentIdx <= 0;
  const atLast = currentIdx >= total - 1;
  return (
    <div class="flex items-center justify-between gap-3 rounded-md border border-border bg-card/40 px-4 py-3">
      <button
        type="button"
        onClick={onPrev}
        disabled={atFirst}
        class="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronLeft width="1.3em" height="1.3em" />
        Anterior
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={atLast}
        class="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        Próxima
        <ChevronRight width="1.3em" height="1.3em" />
      </button>
    </div>
  );
}

// =====================
// Modal de Pausa — adaptado do legado (.prova-paused-overlay/card)
// =====================
// Cobre tudo com backdrop blur enquanto pausada. Cronômetros já param
// pela flag `prova.pausada` nos getters. Esconde o conteúdo da prova
// pra evitar consulta às questões. Sem botão de fechar: só "Continuar"
// ou "Voltar para a Home" (que sai do workspace mantendo pausada=true,
// retomável depois).

function ProvaPausadaModal() {
  // Contextualiza pro aluno: cronômetro atual + tempo desde o último save.
  // Decisão 2026-05-23: a Karol viu "começou agora" porque o tempo total era
  // de 3 min (ela pausou cedo); mostrar isso reduz confusão.
  const ws = provaWorkspace.value;
  const prova = ws?.prova ?? null;
  const tempoTotalMs = prova?.tempoTotalMs ?? 0;
  const tempoTotalLabel = formatHMS(tempoTotalMs);
  const iniciadaEm = prova?.iniciadaEm ?? null;
  const iniciadaLabel = iniciadaEm
    ? new Date(iniciadaEm).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      })
    : null;
  return (
    <div
      class="fixed inset-0 z-[9500] flex items-center justify-center bg-slate-900/72 p-5 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="prova-paused-title"
    >
      <div class="w-full max-w-sm rounded-xl border border-border bg-card px-8 py-7 text-center shadow-2xl">
        <div class="mx-auto mb-3.5 grid size-16 place-items-center rounded-full bg-amber-500/15 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">
          <Pause width="2.25em" height="2.25em" />
        </div>
        <h3 id="prova-paused-title" class="mb-1.5 text-xl font-bold text-foreground">
          Prova pausada
        </h3>
        <p class="mb-3 text-sm leading-relaxed text-muted-foreground">
          O cronômetro está parado. As questões ficam ocultas até você retomar.
        </p>
        {/* Contexto temporal: quando começou e quanto cronometrou efetivamente.
            Reduz a impressão de "começou agora" quando a prova tem dias de
            calendário mas pouco tempo ativo (porque foi pausada cedo). */}
        {(iniciadaLabel || tempoTotalMs > 0) && (
          <div class="mb-5 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            {iniciadaLabel && (
              <div>
                Prova iniciada em <strong>{iniciadaLabel}</strong>
              </div>
            )}
            {tempoTotalMs > 0 && (
              <div>
                Cronômetro acumulado:{" "}
                <strong class="tabular-nums">{tempoTotalLabel}</strong>
                <span class="ml-1 italic opacity-75">
                  (não conta tempo pausado)
                </span>
              </div>
            )}
          </div>
        )}
        <div class="flex flex-col gap-2">
          <button
            type="button"
            onClick={pausarProvaToggle}
            class="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-br from-amber-500 to-amber-600 px-6 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:brightness-105 hover:shadow-lg dark:from-amber-400 dark:to-amber-500 dark:text-amber-950"
          >
            <Play width="0.95em" height="0.95em" />
            Continuar prova
          </button>
          <button
            type="button"
            onClick={() => {
              const bankId = provaWorkspace.value?.bankId ?? null;
              exitProvaWorkspace({ pushHistory: true });
              if (bankId) {
                activeNav.value = "bancos";
                openBank(bankId);
              }
            }}
            title="A prova continua pausada — você pode retomar depois pela tela do banco"
            class="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-transparent px-6 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-foreground/40 hover:bg-accent hover:text-foreground"
          >
            <ChevronLeft width="1em" height="1em" />
            Voltar para o banco
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================
// Painel de Filtros (sliding sheet à direita)
// =====================
// Adaptado do StudyWorkspace.FilterPanel. Diferença: em prova só temos
// "respondida" vs "pendente" (sem acerto/erro), e os filtros atuam sobre
// o ESCOPO já definido quando a prova iniciou, não sobre o banco inteiro.

function ProvaFilterPanel({
  questoes,
  respostas,
  essayResponses,
  essayKeysTouched
}: {
  questoes: QuestaoStudy[];
  respostas: ProvaState["respostas"];
  essayResponses?: Record<string, EssayResponseState>;
  essayKeysTouched?: ReadonlyArray<string> | Set<string>;
}) {
  const isOpen = provaFilterPanelOpen.value;
  const { ref, animStyle, onAnimationEnd, requestClose, isClosing } = useModalGrowShrink(
    isOpen,
    () => (provaFilterPanelOpen.value = false)
  );
  if (!isOpen) return null;
  const f = provaFilters.value;
  const activeCount = countActiveProvaFilters(f);

  // Temas/fontes únicos do ESCOPO (não do banco todo).
  const temas: string[] = [];
  const fontes: string[] = [];
  for (const q of questoes) {
    if (q.tema && !temas.includes(q.tema)) temas.push(q.tema);
    if (q.fonte && !fontes.includes(q.fonte)) fontes.push(q.fonte);
  }

  // Counts de status (respondida/pendente) considerando os outros filtros aplicados.
  const counts = { respondida: 0, pendente: 0 };
  for (const q of questoes) {
    if (
      !matchesProvaFiltro(
        q,
        { ...f, status: [] },
        respostas,
        essayResponses,
        essayKeysTouched
      )
    )
      continue;
    counts[provaQuestaoStatus(q, respostas, essayResponses, essayKeysTouched)]++;
  }

  function toggleArrayItem<T>(arr: T[], item: T): T[] {
    return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
  }

  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) requestClose();
  }

  return (
    <div
      class="modal-overlay-fade fixed inset-0 z-[65] bg-black/40 backdrop-blur-sm"
      data-closing={isClosing ? "true" : "false"}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Filtros da prova"
    >
      <aside
        ref={ref}
        class="absolute right-0 top-0 flex h-full w-full max-w-sm flex-col overflow-y-auto scroll-thin border-l border-border bg-card p-4 shadow-2xl"
        style={animStyle}
        onAnimationEnd={onAnimationEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <header class="mb-4 flex items-center justify-between">
          <h2 class="flex items-center gap-2 text-sm font-bold">
            <SlidersHorizontal width="1.3em" height="1.3em" className="text-primary" />
            Filtros da prova
            {activeCount > 0 && (
              <span class="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold text-primary">
                {activeCount}
              </span>
            )}
          </h2>
          <div class="flex items-center gap-1.5">
            {activeCount > 0 && (
              <button
                type="button"
                onClick={clearProvaFilters}
                class="rounded px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                Limpar
              </button>
            )}
            <button
              type="button"
              aria-label="Fechar"
              onClick={requestClose}
              class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <XIcon className="size-3.5" />
            </button>
          </div>
        </header>

        <div class="space-y-4">
          {/* Busca */}
          <label class="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 focus-within:border-primary">
            <Search width="1.3em" height="1.3em" className="shrink-0 text-muted-foreground" strokeWidth={2} />
            <input
              type="search"
              value={f.busca}
              onInput={(e: Event) =>
                updateProvaFilters({ busca: (e.target as HTMLInputElement).value })
              }
              placeholder="Buscar no enunciado…"
              class="min-w-0 flex-1 bg-transparent py-1.5 text-xs placeholder:text-muted-foreground/60 focus:outline-none"
            />
          </label>

          {/* Status — em prova só respondida/pendente (não há acerto/erro ainda) */}
          <FilterSection title="Status">
            <FilterRow
              label="Respondidas"
              count={counts.respondida}
              checked={f.status.includes("respondida")}
              onToggle={() =>
                updateProvaFilters({ status: toggleArrayItem(f.status, "respondida") })
              }
            />
            <FilterRow
              label="Não respondidas"
              count={counts.pendente}
              checked={f.status.includes("pendente")}
              onToggle={() =>
                updateProvaFilters({ status: toggleArrayItem(f.status, "pendente") })
              }
            />
          </FilterSection>

          {/* Dificuldade */}
          <FilterSection title="Dificuldade">
            {[1, 2, 3, 4, 5].map((d) => (
              <FilterRow
                key={d}
                label={<DifficultyStars value={d} />}
                checked={f.dificuldade.includes(d)}
                onToggle={() =>
                  updateProvaFilters({ dificuldade: toggleArrayItem(f.dificuldade, d) })
                }
              />
            ))}
          </FilterSection>

          {/* Conteúdo */}
          <FilterSection title="Conteúdo">
            <FilterRow
              label={
                <span class="inline-flex items-center gap-1.5">
                  <AlignLeft width="1.3em" height="1.3em" className="text-sky-500" /> Com situação
                </span>
              }
              checked={f.somenteCaso}
              onToggle={() => updateProvaFilters({ somenteCaso: !f.somenteCaso })}
            />
            <FilterRow
              label={
                <span class="inline-flex items-center gap-1.5">
                  <ImageIcon width="1.3em" height="1.3em" className="text-violet-500" /> Com imagem
                </span>
              }
              checked={f.somenteImagem}
              onToggle={() => updateProvaFilters({ somenteImagem: !f.somenteImagem })}
            />
            <FilterRow
              label={
                <span class="inline-flex items-center gap-1.5">
                  <TableIcon width="1.3em" height="1.3em" className="text-teal-500" /> Com tabela
                </span>
              }
              checked={f.somenteTabela}
              onToggle={() => updateProvaFilters({ somenteTabela: !f.somenteTabela })}
            />
          </FilterSection>

          {temas.length > 0 && (
            <FilterSection title={`Tema (${temas.length})`}>
              <div class="max-h-48 space-y-0.5 overflow-y-auto scroll-thin pr-1">
                {temas.map((t) => (
                  <FilterRow
                    key={t}
                    label={t}
                    checked={f.temas.includes(t)}
                    onToggle={() =>
                      updateProvaFilters({ temas: toggleArrayItem(f.temas, t) })
                    }
                  />
                ))}
              </div>
            </FilterSection>
          )}

          {fontes.length > 0 && (
            <FilterSection title={`Fonte (${fontes.length})`}>
              <div class="max-h-48 space-y-0.5 overflow-y-auto scroll-thin pr-1">
                {fontes.map((ft) => (
                  <FilterRow
                    key={ft}
                    label={ft}
                    checked={f.fontes.includes(ft)}
                    onToggle={() =>
                      updateProvaFilters({ fontes: toggleArrayItem(f.fontes, ft) })
                    }
                  />
                ))}
              </div>
            </FilterSection>
          )}
        </div>
      </aside>
    </div>
  );
}

// =====================
// Mapa de Questões (grid clicável)
// =====================
// Adaptado do StudyWorkspace.QuestionMapModal. Diferença: status simplificado
// (respondida/pendente/anulada) — sem cores de acerto/erro, sem botão de
// gabarito (aluno não sabe ainda).

// Modal full-screen mobile com a lista de questões — usado quando a sidebar
// (`hidden lg:flex`) não aparece. Reusa QuestionListBody.
function ProvaListMobileModal({
  questoes,
  currentIdx,
  escopo,
  respostas,
  essayResponses,
  essayKeysTouched,
  visibleIds,
  countLabel,
  agrupamento,
  onAgrupamentoChange,
  onPick
}: {
  questoes: QuestaoStudy[];
  currentIdx: number;
  escopo: QuestaoStudy[];
  respostas: ProvaState["respostas"];
  essayResponses?: Record<string, EssayResponseState>;
  essayKeysTouched?: ReadonlyArray<string> | Set<string>;
  visibleIds: Set<string>;
  countLabel: string;
  agrupamento: QListAgrupamento;
  onAgrupamentoChange: (a: QListAgrupamento) => void;
  onPick: (idx: number) => void;
}) {
  const isOpen = provaListMobileOpen.value;
  const { ref, animStyle, onAnimationEnd, requestClose, isClosing } =
    useModalGrowShrink(isOpen, () => (provaListMobileOpen.value = false));
  const close = requestClose;
  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) close();
  }
  // Mapeia o índice na lista sorted (que o body conhece) pro índice no
  // escopo unsorted (que gotoIdx espera).
  function handlePick(sortedIdx: number) {
    const q = questoes[sortedIdx];
    if (!q) return;
    const unsortedIdx = escopo.findIndex((x) => x.id === q.id);
    if (unsortedIdx >= 0) onPick(unsortedIdx);
  }
  return (
    <div
      class="fixed inset-0 z-[70] grid min-h-full place-items-end overflow-y-auto scroll-thin bg-black/60 backdrop-blur-sm sm:items-center sm:place-items-center sm:p-4 lg:hidden"
      style={{
        animation: isClosing
          ? "modal-backdrop-out 180ms forwards"
          : "modal-backdrop-in 200ms"
      }}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Lista de questões"
    >
      <div
        ref={ref}
        class="flex max-h-[85vh] w-full flex-col overflow-hidden rounded-t-lg border-x border-t border-border bg-card shadow-2xl sm:max-w-lg sm:rounded-md sm:border"
        style={animStyle}
        onAnimationEnd={onAnimationEnd}
      >
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
          <h2 class="text-sm font-bold">
            Questões{" "}
            <span class="text-xs font-medium text-muted-foreground">
              ({countLabel})
            </span>
          </h2>
          <button
            type="button"
            aria-label="Fechar"
            onClick={close}
            class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <XIcon className="size-4" />
          </button>
        </header>

        <div class="space-y-2 border-b border-border px-5 py-3">
          <AnimatedTabs<"tema" | "fonte">
            ariaLabel="Agrupar lista por"
            size="sm"
            variant="strong"
            className="w-full"
            items={[
              { id: "fonte", label: "Fonte" },
              { id: "tema", label: "Tema" }
            ]}
            value={agrupamento === "fonte" ? "fonte" : "tema"}
            onChange={onAgrupamentoChange}
          />
          <QuestionListSearchInput />
        </div>

        <div class="flex flex-1 min-h-0 flex-col overflow-hidden">
          <QuestionListBody
            questoes={questoes}
            currentIdx={escopo[currentIdx] ? questoes.findIndex((q) => q.id === escopo[currentIdx].id) : 0}
            onPick={handlePick}
            statusOf={(q) => {
              if (isAnulada(q)) return "anulada";
              return isProvaRespondida(
                q,
                respostas,
                essayResponses,
                essayKeysTouched
              )
                ? "answered"
                : "pending";
            }}
            tempoMsOf={(q) => respostas[q.id]?.tempoMs ?? 0}
            agrupamento={agrupamento}
            visibleIds={visibleIds}
            numOf={(q: any) => {
              const i = escopo.findIndex((eq) => eq.id === q.id);
              const n = (q as any).numero_original;
              return n != null && n !== "" ? n : i >= 0 ? i + 1 : "?";
            }}
          />
        </div>
      </div>
    </div>
  );
}

function ProvaMapModal({
  questoes,
  respostas,
  essayResponses,
  essayKeysTouched,
  currentIdx,
  onPick
}: {
  questoes: QuestaoStudy[];
  respostas: ProvaState["respostas"];
  essayResponses?: Record<string, EssayResponseState>;
  essayKeysTouched?: ReadonlyArray<string> | Set<string>;
  currentIdx: number;
  onPick: (idx: number) => void;
}) {
  const isOpen = provaMapOpen.value;
  const { ref, animStyle, onAnimationEnd, requestClose, isClosing } = useModalGrowShrink(
    isOpen,
    () => (provaMapOpen.value = false)
  );
  if (!isOpen) return null;

  // Contagens pra legenda — usa isProvaRespondida (mesma fonte que o
  // header) incluindo touched filter pra ignorar bucket Study compartilhado.
  let nR = 0;
  let nP = 0;
  let nA = 0;
  for (const q of questoes) {
    if (isAnulada(q)) nA++;
    else if (
      isProvaRespondida(q, respostas, essayResponses, essayKeysTouched)
    )
      nR++;
    else nP++;
  }

  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) requestClose();
  }

  return (
    <div
      class="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Mapa de questões"
    >
      <div
        ref={ref}
        class="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
        style={animStyle}
        onAnimationEnd={onAnimationEnd}
      >
        <header class="flex items-start justify-between gap-3 border-b border-border px-5 py-3">
          <div class="min-w-0">
            <h2 class="flex items-center gap-2 text-base font-bold">
              <Grid3x3 width="1.3em" height="1.3em" className="text-primary" />
              Mapa de questões
            </h2>
            <div class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
              <span class="inline-flex items-center gap-1">
                <span class="size-2.5 rounded-full bg-primary" /> {nR} respondidas
              </span>
              <span class="inline-flex items-center gap-1">
                <span class="size-2.5 rounded-full bg-muted-foreground/40" /> {nP} pendentes
              </span>
              {nA > 0 && (
                <span class="inline-flex items-center gap-1">
                  <span class="size-2.5 rounded-full bg-amber-500" /> {nA} anuladas
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={requestClose}
            class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <XIcon className="size-4" />
          </button>
        </header>

        <div class="overflow-y-auto p-5 scroll-thin">
          <div class="grid grid-cols-5 gap-2 lg:grid-cols-10">
            {questoes.map((q, i) => (
              <ProvaMapCell
                key={q.id}
                q={q}
                num={i + 1}
                isCurrent={i === currentIdx}
                respondida={isProvaRespondida(
                  q,
                  respostas,
                  essayResponses,
                  essayKeysTouched
                )}
                onClick={() => onPick(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProvaMapCell({
  q,
  num,
  isCurrent,
  respondida,
  onClick
}: {
  q: QuestaoStudy;
  num: number;
  isCurrent: boolean;
  respondida: boolean;
  onClick: () => void;
}) {
  const anulada = isAnulada(q);
  const hasCaso = !!q.caso;
  const hasImg = (typeof q.imagem_url === "string" && q.imagem_url) || (Array.isArray(q.imagens) && q.imagens.length > 0);
  const hasTab = Array.isArray(q.tabelas) && q.tabelas.length > 0;

  const tone = anulada
    ? "border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300"
    : respondida
      ? "border-primary/40 bg-primary/10 text-primary"
      : "border-border bg-card/60 text-foreground/80 hover:bg-accent/60";

  return (
    <button
      type="button"
      onClick={onClick}
      title={`Questão ${num}${q.tema ? ` · ${q.tema}` : ""}`}
      class={`relative flex aspect-square min-h-[56px] flex-col items-center justify-center rounded-md border text-sm font-bold transition-all ${tone} ${
        isCurrent ? "ring-2 ring-primary scale-105" : ""
      }`}
    >
      <span class="leading-none">{num}</span>
      {/* Mini-pílulas tipadas (sky=Situação, violet=Imagem, teal=Tabela).
          Compactas pra caber sem sobrepor em cells de ~50px (grid-cols-5
          em mobile). Mantém a cor reconhecível mesmo sem o ícone óbvio. */}
      <span class="mt-0.5 flex max-w-full flex-wrap items-center justify-center gap-px">
        {hasCaso && (
          <span
            title="Situação"
            class="grid size-2.5 place-items-center rounded-[2px] bg-sky-500/30 text-sky-600 dark:bg-sky-500/35 dark:text-sky-300"
          >
            <AlignLeft width="6" height="6" strokeWidth={2.8} />
          </span>
        )}
        {hasImg && (
          <span
            title="Imagem"
            class="grid size-2.5 place-items-center rounded-[2px] bg-violet-500/30 text-violet-600 dark:bg-violet-500/35 dark:text-violet-300"
          >
            <ImageIcon width="6" height="6" strokeWidth={2.8} />
          </span>
        )}
        {hasTab && (
          <span
            title="Tabela"
            class="grid size-2.5 place-items-center rounded-[2px] bg-teal-500/30 text-teal-600 dark:bg-teal-500/35 dark:text-teal-300"
          >
            <TableIcon width="6" height="6" strokeWidth={2.8} />
          </span>
        )}
      </span>
    </button>
  );
}


function ProvaEncerrarConfirm({
  respondidas,
  total
}: {
  respondidas: number;
  total: number;
}) {
  const { ref, animStyle, onAnimationEnd, requestClose, isClosing } = useModalGrowShrink(
    true,
    () => (provaEncerrarConfirmOpen.value = false)
  );
  const pendentes = total - respondidas;
  return (
    <div
      class="fixed inset-0 z-[60] grid min-h-full place-items-center overflow-hidden bg-black/60 p-4 backdrop-blur-sm"
      style={{ animation: isClosing ? "modal-backdrop-out 180ms forwards" : "modal-backdrop-in 200ms" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        ref={ref}
        class="w-full max-w-md rounded-md border border-border bg-card shadow-2xl"
        style={animStyle}
        onAnimationEnd={onAnimationEnd}
      >
        <header class="flex items-center justify-between border-b border-border px-5 py-3.5">
          <div class="flex items-center gap-2 text-base font-bold">
            <Flag width="1.3em" height="1.3em" className="text-primary" />
            Encerrar simulação?
          </div>
          <button
            type="button"
            onClick={requestClose}
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <XIcon className="size-4" />
          </button>
        </header>
        <div class="space-y-3 px-5 py-4 text-sm text-foreground/90">
          <p>
            Você respondeu{" "}
            <span class="font-semibold">{respondidas}</span> de{" "}
            <span class="font-semibold">{total}</span> questões.
          </p>
          {pendentes > 0 && (
            <p class="text-muted-foreground">
              {pendentes === 1
                ? "1 questão fica em branco e contará como erro."
                : `${pendentes} questões ficam em branco e contarão como erro.`}
            </p>
          )}
          <p class="text-muted-foreground">Esta ação não pode ser desfeita.</p>
        </div>
        <footer class="flex items-center justify-end gap-2 border-t border-border bg-card/50 px-5 py-3">
          <button
            type="button"
            onClick={requestClose}
            class="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Continuar simulação
          </button>
          <button
            type="button"
            onClick={() => {
              provaEncerrarConfirmOpen.value = false;
              void encerrarProva("manual");
            }}
            class="rounded-md bg-red-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-red-600"
          >
            Encerrar agora
          </button>
        </footer>
      </div>
    </div>
  );
}
